import type { ChatMessage } from "./api";
import { TOOL_DEFS, type ToolCall } from "./tools";

/**
 * Настройки провайдера LLM.
 *
 * Живут ТОЛЬКО в localStorage: пасты публичны, ключ в них попасть не должен.
 * Пользовательский код исполняется на отдельном origin (uc.*), поэтому
 * прочитать localStorage основного домена он не может.
 */
export interface AiSettings {
  baseUrl: string;
  apiKey: string;
  model: string;
  temperature: number;
  maxTokens: number;
  /**
   * Не передавать `max_tokens` вовсе — провайдер применит свой максимум.
   * По умолчанию включено: reasoning-модели иначе упираются в лимит.
   */
  autoMaxTokens: boolean;
  /** Разрешить модели вызывать инструменты (файлы, execute_js). */
  useTools: boolean;
  systemPrompt: string;
}

const STORAGE_KEY = "vetchka:ai";

export const DEFAULT_SETTINGS: AiSettings = {
  baseUrl: "",
  apiKey: "",
  model: "",
  temperature: 0.2,
  maxTokens: 8192,
  autoMaxTokens: true,
  useTools: true,
  systemPrompt:
    "You are a coding assistant inside a browser code sandbox. " +
    "Answer concisely and use fenced code blocks. " +
    "You can edit files with tools (write_file/read_file/list_files/delete_file), " +
    "run JavaScript in the live preview with execute_js, and read runtime problems with " +
    "get_console_errors — prefer fixing errors yourself instead of asking the user to copy them. " +
    "Use set_title to name the paste when asked or when its purpose becomes clear.",
};

export function loadSettings(): AiSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return { ...DEFAULT_SETTINGS };
    return { ...DEFAULT_SETTINGS, ...(parsed as Partial<AiSettings>) };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export function saveSettings(settings: AiSettings): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
}

/** Свой провайдер настроен (ключ/baseUrl в браузере). */
export function isConfigured(s: AiSettings): boolean {
  return s.baseUrl.trim() !== "" && s.model.trim() !== "";
}

/**
 * Приводит base url к виду без хвостового слэша и без `/chat/completions`.
 *
 * Люди часто копируют из документации полный адрес эндпоинта — вырезаем его
 * сами, чтобы не получить `/chat/completions/chat/completions`.
 */
export function normalizeBaseUrl(url: string): string {
  let out = url.trim().replace(/\/+$/, "");
  out = out.replace(/\/chat\/completions$/i, "");
  return out.replace(/\/+$/, "");
}

function endpoint(baseUrl: string): string {
  return `${normalizeBaseUrl(baseUrl)}/chat/completions`;
}

export class AiError extends Error {}

/** Итог одного ответа модели. */
export interface CompletionResult {
  text: string;
  /** Цепочка рассуждений (reasoning-модели). */
  reasoning: string;
  /** stop | length | tool_calls | content_filter | null */
  finishReason: string | null;
  /** Запрошенные моделью вызовы инструментов. */
  toolCalls: ToolCall[];
}

export interface StreamHooks {
  onDelta: (text: string) => void;
  onReasoning?: (text: string) => void;
}

export type ProviderMessage =
  | { role: "system" | "user"; content: string }
  | { role: "assistant"; content: string; tool_calls?: ToolCall[] }
  | { role: "tool"; content: string; tool_call_id: string };

export interface StreamOptions extends StreamHooks {
  settings: AiSettings;
  messages: ProviderMessage[];
  signal?: AbortSignal;
}

export interface ServerProviderConfig {
  enabled: boolean;
  model: string;
}

/** Каким способом ходим к модели. */
export type AiMode = "direct" | "server" | "none";

export function resolveMode(
  settings: AiSettings,
  server: ServerProviderConfig | null,
): AiMode {
  if (isConfigured(settings)) return "direct";
  if (server?.enabled) return "server";
  return "none";
}

/**
 * Стримит ответ OpenAI-compatible провайдера прямо из браузера.
 * Ключ никуда не уходит, кроме самого провайдера.
 */
export async function streamChat(opts: StreamOptions): Promise<CompletionResult> {
  const { settings, messages, signal } = opts;
  if (!isConfigured(settings)) throw new AiError("provider is not configured");
  const tools = settings.useTools ? TOOL_DEFS : null;

  const res = await fetch(endpoint(settings.baseUrl), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${settings.apiKey}`,
      Accept: "text/event-stream",
    },
    body: JSON.stringify({
      model: settings.model,
      messages,
      stream: true,
      temperature: settings.temperature,
      // auto: не передаём вовсе, провайдер подставит свой максимум
      max_tokens: settings.autoMaxTokens ? undefined : settings.maxTokens,
      ...(tools ? { tools, tool_choice: "auto" } : {}),
    }),
    signal,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new AiError(`${res.status} ${res.statusText}${text ? `: ${text.slice(0, 300)}` : ""}`);
  }
  return consumeCompletion(res, opts);
}

export interface ServerChatOptions extends StreamHooks {
  messages: ProviderMessage[];
  temperature?: number;
  maxTokens?: number;
  tools?: readonly unknown[];
  signal?: AbortSignal;
}

/**
 * Стримит через наш сервер: ключ провайдера остаётся на сервере.
 *
 * Нужно там, где браузерный запрос к провайдеру невозможен — например,
 * CORS или Chrome Local Network Access (публичный сайт → приватный адрес).
 */
export async function streamChatViaServer(opts: ServerChatOptions): Promise<CompletionResult> {
  const res = await fetch("/api/ai/chat", {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
    body: JSON.stringify({
      messages: opts.messages,
      temperature: opts.temperature,
      // undefined = auto, сервер не будет подставлять своё значение
      maxTokens: opts.maxTokens,
      tools: opts.tools,
    }),
    signal: opts.signal,
  });

  if (!res.ok) {
    if (res.status === 503) throw new AiError("серверный провайдер не настроен");
    if (res.status === 429) throw new AiError("слишком много запросов к серверному провайдеру, попробуйте позже");
    let detail = "";
    let upstreamStatus: number | undefined;
    try {
      const body = (await res.json()) as {
        error?: string;
        message?: string;
        status?: number;
      };
      upstreamStatus = body.status;
      detail = (body.message || body.error || "").replace(/\s+/g, " ").slice(0, 200);
    } catch {
      /* ignore */
    }
    if (upstreamStatus) {
      throw new AiError(
        `провайдер недоступен с сервера (${upstreamStatus}). ${detail}`,
      );
    }
    throw new AiError(`${res.status} ${res.statusText}${detail ? `: ${detail}` : ""}`);
  }

  return consumeCompletion(res, opts);
}

/**
 * Разбирает ответ провайдера: SSE (delta/message/text) или один JSON.
 *
 * Reasoning-модели стримят рассуждения отдельным полем, а финальный текст
 * может не прийти вовсе, если `max_tokens` израсходован на размышления
 * (`finish_reason: "length"`). Поэтому собираем и то, и другое.
 */
async function consumeCompletion(
  res: Response,
  hooks: StreamHooks,
): Promise<CompletionResult> {
  if (!res.body) throw new AiError("no response body");

  let text = "";
  let reasoning = "";
  let finishReason: string | null = null;
  let sawSse = false;
  let raw = "";
  const toolCalls: ToolCall[] = [];

  const emit = (payload: string) => {
    if (payload === "" || payload === "[DONE]") return;
    let json: unknown;
    try {
      json = JSON.parse(payload);
    } catch {
      return;
    }
    const part = extractParts(json);
    if (part.finishReason) finishReason = part.finishReason;
    if (part.content) {
      text += part.content;
      hooks.onDelta(part.content);
    }
    if (part.reasoning) {
      reasoning += part.reasoning;
      hooks.onReasoning?.(part.reasoning);
    }
    if (part.toolCalls?.length) mergeToolCalls(toolCalls, part.toolCalls);
  };

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    const chunk = decoder.decode(value, { stream: true });
    raw += chunk;
    buffer += chunk;

    let idx: number;
    while ((idx = buffer.indexOf("\n")) >= 0) {
      const line = buffer.slice(0, idx).replace(/\r$/, "");
      buffer = buffer.slice(idx + 1);
      if (!line.startsWith("data:")) continue;
      sawSse = true;
      emit(line.slice(5).trim());
    }
  }

  const tail = buffer.replace(/\r$/, "").trim();
  if (tail.startsWith("data:")) {
    sawSse = true;
    emit(tail.slice(5).trim());
  }

  // Провайдер мог проигнорировать stream:true и вернуть один JSON-объект.
  if (!sawSse) {
    const body = raw.trim();
    if (body !== "") {
      let json: unknown;
      try {
        json = JSON.parse(body);
      } catch {
        throw new AiError(`unexpected response: ${body.slice(0, 200)}`);
      }
      const err = extractError(json);
      if (err) throw new AiError(err);
      const part = extractParts(json);
      if (part.finishReason) finishReason = part.finishReason;
      if (part.content) {
        text = part.content;
        hooks.onDelta(part.content);
      }
      if (part.reasoning) {
        reasoning = part.reasoning;
        hooks.onReasoning?.(part.reasoning);
      }
      if (part.toolCalls?.length) mergeToolCalls(toolCalls, part.toolCalls);
    }
  }

  const calls = toolCalls.filter((c) => c && c.function.name !== "");

  // Совсем ничего — вот это уже ошибка.
  if (text === "" && reasoning === "" && finishReason === null && calls.length === 0) {
    const err = raw ? extractError(safeJson(raw)) : null;
    if (err) throw new AiError(err);
    throw new AiError(
      raw
        ? `provider returned no text: ${raw.replace(/\s+/g, " ").slice(0, 240)}`
        : "provider returned an empty response",
    );
  }

  return { text, reasoning, finishReason, toolCalls: calls };
}

interface RawToolCallDelta {
  index?: number;
  id?: string;
  type?: string;
  function?: { name?: string; arguments?: string };
}

/** Собирает стримящиеся фрагменты tool_calls по индексу. */
function mergeToolCalls(acc: ToolCall[], deltas: RawToolCallDelta[]): void {
  for (const d of deltas) {
    const idx = typeof d.index === "number" ? d.index : acc.length;
    let call = acc[idx];
    if (!call) {
      call = { id: d.id ?? `call_${idx}`, type: "function", function: { name: "", arguments: "" } };
      acc[idx] = call;
    }
    if (d.id) call.id = d.id;
    if (d.function?.name) call.function.name = d.function.name;
    if (d.function?.arguments) call.function.arguments += d.function.arguments;
  }
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

interface Parts {
  content: string;
  reasoning: string;
  finishReason: string | null;
  toolCalls?: RawToolCallDelta[];
}

/** Текст ответа: streaming (delta), non-streaming (message) или legacy (text). */
function extractParts(json: unknown): Parts {
  const obj = json as
    | {
        choices?: Array<{
          delta?: {
            content?: unknown;
            reasoning?: unknown;
            reasoning_content?: unknown;
            tool_calls?: RawToolCallDelta[];
          };
          message?: {
            content?: unknown;
            reasoning?: unknown;
            tool_calls?: RawToolCallDelta[];
          };
          text?: unknown;
          finish_reason?: unknown;
        }>;
      }
    | null;
  const choice = obj?.choices?.[0];
  if (!choice) return { content: "", reasoning: "", finishReason: null };

  const content = pickText([choice.delta?.content, choice.message?.content, choice.text]);
  const reasoning = pickText([
    choice.delta?.reasoning,
    choice.delta?.reasoning_content,
    choice.message?.reasoning,
  ]);
  const finishReason =
    typeof choice.finish_reason === "string" ? choice.finish_reason : null;
  const toolCalls = choice.delta?.tool_calls ?? choice.message?.tool_calls;

  return { content, reasoning, finishReason, toolCalls };
}

/** Достаёт строку из значения: строка, массив частей {text} или пусто. */
function pickText(candidates: unknown[]): string {
  for (const c of candidates) {
    if (typeof c === "string" && c !== "") return c;
    if (Array.isArray(c)) {
      const joined = c
        .map((part) =>
          typeof part === "string"
            ? part
            : typeof (part as { text?: unknown })?.text === "string"
              ? (part as { text: string }).text
              : "",
        )
        .join("");
      if (joined !== "") return joined;
    }
  }
  return "";
}

function extractError(json: unknown): string | null {
  const obj = json as { error?: unknown } | null;
  const err = obj?.error;
  if (!err) return null;
  if (typeof err === "string") return err;
  const message = (err as { message?: unknown })?.message;
  return typeof message === "string" ? message : JSON.stringify(err).slice(0, 300);
}

/** Проверка настроек: тянем список моделей, чтобы заодно проверить CORS. */
export async function listModels(settings: AiSettings): Promise<string[]> {
  const url = `${normalizeBaseUrl(settings.baseUrl)}/models`;
  const res = await fetch(url, {
    headers: settings.apiKey ? { Authorization: `Bearer ${settings.apiKey}` } : {},
  });
  if (!res.ok) throw new AiError(`${res.status} ${res.statusText}`);
  const json = (await res.json()) as { data?: Array<{ id?: string }> };
  return (json.data ?? []).map((m) => m.id ?? "").filter(Boolean);
}

/**
 * Собирает сообщения для провайдера: системный промпт с контекстом паста +
 * история чата, включая tool-calls и их результаты.
 */
export function buildRequestMessages(opts: {
  settings: AiSettings;
  title: string;
  files: Array<{ path: string; content: string }>;
  activePath: string;
  history: ChatMessage[];
  historyLimit?: number;
}): ProviderMessage[] {
  const { settings, title, files, activePath, history, historyLimit = 40 } = opts;

  const paths = files.map((f) => `- ${f.path}`).join("\n");
  const active = files.find((f) => f.path === activePath);

  const context = [
    settings.systemPrompt,
    "",
    `Current paste title: ${title.trim() === "" ? "(untitled)" : title}`,
    "Files in the paste:",
    paths || "(none)",
  ];
  if (active) {
    context.push("", `Active file: ${active.path}`, "```", active.content, "```");
  }

  const out: ProviderMessage[] = [{ role: "system", content: context.join("\n") }];

  const recent = history.slice(-historyLimit);
  // Не начинаем историю с tool-сообщения без предшествующего вызова.
  while (recent.length > 0 && recent[0]!.role === "tool") recent.shift();

  for (const m of recent) {
    if (m.role === "user") {
      out.push({ role: "user", content: m.content });
    } else if (m.role === "assistant") {
      const calls = toolCallsOf(m);
      if (m.content === "" && calls.length === 0) continue;
      out.push({
        role: "assistant",
        content: m.content,
        ...(calls.length > 0 ? { tool_calls: calls } : {}),
      });
    } else if (m.role === "tool") {
      out.push({
        role: "tool",
        content: m.content,
        tool_call_id: toolCallIdOf(m),
      });
    }
  }

  return out;
}

interface HistoryMeta {
  toolCalls?: ToolCall[];
  toolCallId?: string;
}

function historyMeta(m: ChatMessage): HistoryMeta {
  return (m.meta && typeof m.meta === "object" ? m.meta : {}) as HistoryMeta;
}

export function toolCallsOf(m: ChatMessage): ToolCall[] {
  const calls = historyMeta(m).toolCalls;
  return Array.isArray(calls) ? calls : [];
}

export function toolCallIdOf(m: ChatMessage): string {
  return historyMeta(m).toolCallId ?? "";
}
